/** Locked GHS 16-section keys + labels (MSDS-3). */

export const MFG_SDS_SECTION_KEYS = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
] as const;

export type MfgSdsSectionKey = (typeof MFG_SDS_SECTION_KEYS)[number];

export const MFG_SDS_SECTION_LABELS: Readonly<Record<MfgSdsSectionKey, string>> = {
  "1": "Identification",
  "2": "Hazard(s) identification",
  "3": "Composition / information on ingredients",
  "4": "First-aid measures",
  "5": "Fire-fighting measures",
  "6": "Accidental release measures",
  "7": "Handling and storage",
  "8": "Exposure controls / personal protection",
  "9": "Physical and chemical properties",
  "10": "Stability and reactivity",
  "11": "Toxicological information",
  "12": "Ecological information",
  "13": "Disposal considerations",
  "14": "Transport information",
  "15": "Regulatory information",
  "16": "Other information",
};

export type MfgSdsSections = Partial<Record<MfgSdsSectionKey, string>>;

export type MfgSdsSectionAuditStatus = "ai_draft" | "opened" | "edited";

export type MfgSdsSectionAuditEntry = {
  status: MfgSdsSectionAuditStatus;
  last_touched_by: string;
  last_touched_at: string;
};

export type MfgSdsSectionAudit = Partial<Record<MfgSdsSectionKey, MfgSdsSectionAuditEntry>>;

export function emptySdsSections(): Record<MfgSdsSectionKey, string> {
  const out = {} as Record<MfgSdsSectionKey, string>;
  for (const key of MFG_SDS_SECTION_KEYS) out[key] = "";
  return out;
}

export function isMfgSdsSectionKey(value: unknown): value is MfgSdsSectionKey {
  return typeof value === "string" && (MFG_SDS_SECTION_KEYS as readonly string[]).includes(value);
}

/** Normalize unknown jsonb into a full "1".."16" string map. */
export function normalizeSdsSections(raw: unknown): Record<MfgSdsSectionKey, string> {
  const out = emptySdsSections();
  if (!raw || typeof raw !== "object") return out;
  const row = raw as Record<string, unknown>;
  for (const key of MFG_SDS_SECTION_KEYS) {
    const v = row[key];
    out[key] = typeof v === "string" ? v : "";
  }
  return out;
}

export function normalizeSdsSectionAudit(raw: unknown): MfgSdsSectionAudit {
  if (!raw || typeof raw !== "object") return {};
  const row = raw as Record<string, unknown>;
  const out: MfgSdsSectionAudit = {};
  for (const key of MFG_SDS_SECTION_KEYS) {
    const entry = row[key];
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const status = e.status;
    if (status !== "ai_draft" && status !== "opened" && status !== "edited") continue;
    if (typeof e.last_touched_by !== "string" || typeof e.last_touched_at !== "string") continue;
    out[key] = {
      status,
      last_touched_by: e.last_touched_by,
      last_touched_at: e.last_touched_at,
    };
  }
  return out;
}
